/**
 * ISO-Standard ID Generator für ERP-Systeme
 * Implementiert sequenzielle Nummern nach ISO-Standards
 */

import { getCurrentBusinessId } from './sharedCore';
import { readSalesSyncForBusiness } from './salesReadSource';
import { readCustomersSyncForBusiness } from './customersReadSource';
import { readPurchasesSyncForBusiness } from './purchasesReadSource';
import { readOrdersSyncForBusiness } from './ordersReadSource';
import { readSuppliersSyncForBusiness } from './suppliersReadSource';
import { readExpensesSyncForBusiness } from './expensesReadSource';
import { readSupermarketInvoicesSyncForBusiness } from './supermarketInvoicesReadSource';
import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';
import { getStorageEngine } from '../app/di/container';
import {
  formatConsolidatedInvoiceNumber,
  formatNormalInvoiceNumber,
  bumpSequenceMapFromInvoices,
  consolidatedInvoiceSequenceKey,
  normalInvoiceSequenceKey
} from './invoiceNumberFormat';
import { loadWorkersForBusiness } from './workersStorage';
import { readSupermarketPaymentsSyncForBusiness } from './supermarketPaymentsReadSource';
import { readInvoicePaymentsSyncForBusiness } from './invoicePaymentsReadSource';

async function flushKvBackend() {
  const engine = getStorageEngine();
  if (engine._backend?.flushPendingWrites) {
    await engine._backend.flushPendingWrites();
  }
}

function parseSequenceFromId(id, prefix) {
  if (id == null) return 0;
  const s = String(id).trim();
  const anchored = new RegExp(`^${prefix}-\\d{4}-(\\d+)$`, 'i');
  let m = s.match(anchored);
  if (m) return parseInt(m[1], 10);
  const embedded = new RegExp(`${prefix}-(\\d{4})-(\\d+)`, 'i');
  m = s.match(embedded);
  return m ? parseInt(m[2], 10) : 0;
}

function bumpMaxSequenceFromRecords(records, fields, type, prefix, sequences) {
  let max = sequences.get(type) || 0;
  const list = Array.isArray(records) ? records : [];
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    for (const field of fields) {
      max = Math.max(max, parseSequenceFromId(row[field], prefix));
    }
  }
  if (max > (sequences.get(type) || 0)) {
    sequences.set(type, max);
  }
}

class ISOStandardIdGenerator {
  constructor() {
    this.sequences = new Map();
    this._sequenceBusinessId = null;
    /** In-memory reservations until syncSequencesAfterPersist (multi-tab / concurrent save). */
    this._inflightAllocations = new Map();
    this.initializeDefaultSequences({ persist: false });
  }

  _inflightKey(businessId, type) {
    return `${businessId || 'global'}:${type}`;
  }

  clearInflightAllocations(businessId) {
    if (!businessId) return;
    const prefix = `${businessId}:`;
    for (const key of [...this._inflightAllocations.keys()]) {
      if (key.startsWith(prefix)) {
        this._inflightAllocations.delete(key);
      }
    }
  }

  /**
   * Initialisiert Sequenzen für alle ID-Typen (nur wenn Business eingeloggt).
   */
  initializeSequences() {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    if (!businessId) return;
    const sequenceKey = businessId;
    
    // Lade bestehende Sequenzen aus KV (Phase 0: weiterhin localStorage-Backend)
    const storedSequences = kvGet(`sequences_${sequenceKey}`);
    if (storedSequences != null) {
      try {
        const sequences =
          typeof storedSequences === 'object' &&
          storedSequences !== null &&
          !Array.isArray(storedSequences)
            ? storedSequences
            : typeof storedSequences === 'string'
              ? JSON.parse(storedSequences)
              : null;
        if (sequences && typeof sequences === 'object') {
          this.sequences = new Map(Object.entries(sequences));
          this._sequenceBusinessId = businessId;
          this.syncSequencesFromBusinessPayload(businessId);
          return;
        }
      } catch (error) {
        console.warn('Error loading sequences:', error);
      }
    }
    this._sequenceBusinessId = businessId;
    this.initializeDefaultSequences({ persist: false });
    this.syncSequencesFromBusinessPayload(businessId);
    this.saveSequences();
  }

  /**
   * Initialisiert Standard-Sequenzen
   * @param {{ persist?: boolean }} [options]
   */
  initializeDefaultSequences(options = {}) {
    const { persist = true } = options;
    const currentYear = new Date().getFullYear();
    
    this.sequences.set('ACHAT', 0);
    this.sequences.set('VENTE', 0);
    this.sequences.set('COMMANDE', 0);
    this.sequences.set('CLIENT', 0);
    this.sequences.set('FOURNISSEUR', 0);
    this.sequences.set('TRAVAILLEUR', 0);
    this.sequences.set('INVOICE', 0);
    this.sequences.set('RECEIPT', 0);
    this.sequences.set('DEPENSE', 0);

    if (persist) this.saveSequences();
  }

  /**
   * Generiert ISO-konforme ACHAT-ID
   * Format: ACHAT-YYYY-NNNNNN
   */
  generateAchatId() {
    return this.generateSequentialId('ACHAT', 'ACHAT');
  }

  /**
   * Generiert ISO-konforme VENTE-ID
   * Format: VENTE-YYYY-NNNNNN
   */
  generateVenteId() {
    return this.generateSequentialId('VENTE', 'VENTE');
  }

  /**
   * Generiert ISO-konforme COMMANDE-ID
   * Format: CMD-YYYY-NNNNNN
   */
  generateCommandeId() {
    return this.generateSequentialId('COMMANDE', 'CMD');
  }

  /**
   * Generiert ISO-konforme CLIENT-ID
   * Format: CLIENT-YYYY-NNNNNN
   */
  generateClientId() {
    return this.generateSequentialId('CLIENT', 'CLIENT');
  }

  /**
   * Generiert ISO-konforme FOURNISSEUR-ID
   * Format: FOUR-YYYY-NNNNNN
   */
  generateFournisseurId() {
    return this.generateSequentialId('FOURNISSEUR', 'FOUR');
  }

  /**
   * Generiert ISO-konforme TRAVAILLEUR-ID
   * Format: TRAV-YYYY-NNNNNN
   */
  generateTravailleurId() {
    return this.generateSequentialId('TRAVAILLEUR', 'TRAV');
  }

  /**
   * Facture normale — Format: YYYY.NNN … YYYY.NNNN (3 puis 4 chiffres)
   */
  generateNormalInvoiceNumber() {
    return this.generateYearlyInvoiceNumber(normalInvoiceSequenceKey, formatNormalInvoiceNumber);
  }

  /**
   * Facture consolidée — Format: CON YYYY.NNNN (ex. CON 2026.0001)
   */
  generateConsolidatedInvoiceNumber() {
    return this.generateYearlyInvoiceNumber(
      consolidatedInvoiceSequenceKey,
      formatConsolidatedInvoiceNumber
    );
  }

  /**
   * @deprecated Alias — facture normale
   */
  generateInvoiceNumber() {
    return this.generateNormalInvoiceNumber();
  }

  /**
   * Nächste Rechnungsnummer berechnen — ohne KV-Zähler zu erhöhen.
   * Zähler wird erst nach persistiertem Datensatz via syncSequencesFromBusinessPayload aktualisiert.
   */
  peekNextYearlyInvoiceNumber(keyFn, formatter) {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    const base = this.buildSequenceMapFromRecords(businessId);
    const year = new Date().getFullYear();
    const key = keyFn(year);
    const currentSequence = base.get(key) || 0;
    const nextSequence = currentSequence + 1;
    return formatter(year, nextSequence);
  }

  generateYearlyInvoiceNumber(keyFn, formatter) {
    return this.peekNextYearlyInvoiceNumber(keyFn, formatter);
  }

  syncInvoiceSequencesFromRecords(invoices) {
    if (!Array.isArray(invoices) || invoices.length === 0) return;
    this.sequences = bumpSequenceMapFromInvoices(invoices, this.sequences);
    this.saveSequences();
  }

  /**
   * Generiert ISO-konforme Quittungsnummer (Französisch)
   * Format: QUITT-YYYY-NNNNNN (sequenziell)
   * HINWEIS: Quittungsnummern sind normalerweise die gleichen wie Vente-IDs
   */
  generateReceiptNumber() {
    // Quittungsnummern sind normalerweise die gleichen wie Vente-IDs
    // Aber falls eine separate Quittungsnummer benötigt wird:
    return this.generateSequentialId('RECEIPT', 'QUITT');
  }

  /**
   * Generiert Quittungsnummer basierend auf Vente-ID
   * Format: Gleiche wie Vente-ID (VENTE-YYYY-NNNNNN)
   */
  generateReceiptFromVente(venteId) {
    // Quittungsnummer ist die gleiche wie Vente-ID
    return venteId;
  }

  /**
   * Dépense — Format: DEP-YYYY-NNNNNN
   */
  generateDepenseId() {
    return this.generateSequentialId('DEPENSE', 'DEP');
  }

  /**
   * Sequenzen aus KV + bestehenden Entity-Daten nachladen (IndexedDB-sicher).
   */
  reloadSequences() {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    if (!businessId) return;
    const sequenceKey = businessId;
    this._sequenceBusinessId = businessId;
    const storedSequences = kvGet(`sequences_${sequenceKey}`);

    if (storedSequences != null) {
      try {
        const sequences =
          typeof storedSequences === 'object' &&
          storedSequences !== null &&
          !Array.isArray(storedSequences)
            ? storedSequences
            : typeof storedSequences === 'string'
              ? JSON.parse(storedSequences)
              : null;
        if (sequences && typeof sequences === 'object') {
          this.sequences = new Map(Object.entries(sequences));
        }
      } catch (error) {
        console.warn('reloadSequences: parse failed', error);
      }
    }

    this.syncSequencesFromBusinessPayload(businessId);
  }

  /**
   * Sequenz-Stand ausschließlich aus persistierten Datensätzen (heilt aufgeblähte KV-Zähler).
   */
  buildSequenceMapFromRecords(businessId) {
    const sequences = new Map([
      ['ACHAT', 0],
      ['VENTE', 0],
      ['COMMANDE', 0],
      ['CLIENT', 0],
      ['FOURNISSEUR', 0],
      ['TRAVAILLEUR', 0],
      ['INVOICE', 0],
      ['RECEIPT', 0],
      ['DEPENSE', 0]
    ]);

    if (!businessId) return sequences;

    try {
      bumpMaxSequenceFromRecords(
        readSalesSyncForBusiness(businessId),
        ['orderId', 'id'],
        'VENTE',
        'VENTE',
        sequences
      );
      bumpMaxSequenceFromRecords(
        readCustomersSyncForBusiness(businessId),
        ['customerId', 'id'],
        'CLIENT',
        'CLIENT',
        sequences
      );
      bumpMaxSequenceFromRecords(
        readPurchasesSyncForBusiness(businessId),
        ['purchaseId', 'id'],
        'ACHAT',
        'ACHAT',
        sequences
      );
      bumpMaxSequenceFromRecords(
        readOrdersSyncForBusiness(businessId),
        ['orderId', 'id'],
        'COMMANDE',
        'CMD',
        sequences
      );
      bumpMaxSequenceFromRecords(
        readSuppliersSyncForBusiness(businessId),
        ['supplierId', 'id'],
        'FOURNISSEUR',
        'FOUR',
        sequences
      );
      bumpMaxSequenceFromRecords(
        readExpensesSyncForBusiness(businessId),
        ['id', 'expenseId'],
        'DEPENSE',
        'DEP',
        sequences
      );

      try {
        const { workers } = loadWorkersForBusiness(businessId);
        bumpMaxSequenceFromRecords(
          workers,
          ['id', 'workerId'],
          'TRAVAILLEUR',
          'TRAV',
          sequences
        );
      } catch {
        /* optional */
      }

      try {
        const supermarketPayments = readSupermarketPaymentsSyncForBusiness(businessId);
        bumpMaxSequenceFromRecords(
          supermarketPayments,
          ['receiptNumber'],
          'RECEIPT',
          'QUITT',
          sequences
        );
        const invoicePayments = readInvoicePaymentsSyncForBusiness(businessId);
        bumpMaxSequenceFromRecords(
          invoicePayments,
          ['receiptNumber'],
          'RECEIPT',
          'QUITT',
          sequences
        );
      } catch {
        /* optional */
      }

      try {
        const invoices = readSupermarketInvoicesSyncForBusiness(businessId);
        if (Array.isArray(invoices) && invoices.length > 0) {
          const merged = bumpSequenceMapFromInvoices(invoices, sequences);
          if (merged instanceof Map) {
            for (const [k, v] of merged.entries()) {
              sequences.set(k, v);
            }
          }
        }
      } catch {
        /* optional */
      }
    } catch (error) {
      console.warn('buildSequenceMapFromRecords failed:', error);
    }

    return sequences;
  }

  syncSequencesFromBusinessPayload(businessId) {
    if (!businessId) return;
    try {
      this.sequences = this.buildSequenceMapFromRecords(businessId);
      this._sequenceBusinessId = businessId;
      this.clearInflightAllocations(businessId);
      this.saveSequences();
    } catch (error) {
      console.warn('syncSequencesFromBusinessPayload failed:', error);
    }
  }

  /**
   * Nächste sequenzielle ID berechnen — ohne KV-Zähler zu erhöhen.
   * Basis: nur gespeicherte Datensätze (Entwürfe / Abbruch verbrauchen keine Nummer).
   */
  peekNextSequentialId(type, prefix) {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    const base = this.buildSequenceMapFromRecords(businessId);
    const currentYear = new Date().getFullYear();
    const currentSequence = base.get(type) || 0;
    const nextSequence = currentSequence + 1;
    const paddedSequence = nextSequence.toString().padStart(6, '0');
    return `${prefix}-${currentYear}-${paddedSequence}`;
  }

  /**
   * Nächste ID wie allocateSequentialId — ohne inflight zu erhöhen (Vorschau / Peek).
   */
  peekAllocateSequentialId(type, prefix) {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    const base = this.buildSequenceMapFromRecords(businessId);
    const inflightKey = this._inflightKey(businessId, type);
    const inflight = this._inflightAllocations.get(inflightKey) || 0;
    const currentYear = new Date().getFullYear();
    const currentSequence = base.get(type) || 0;
    const nextSequence = currentSequence + inflight + 1;
    const paddedSequence = nextSequence.toString().padStart(6, '0');
    return `${prefix}-${currentYear}-${paddedSequence}`;
  }

  /**
   * Reserviert die nächste ID im Speicher (bis syncSequencesAfterPersist).
   * Verhindert Duplikate bei parallelen Saves / Tabs.
   */
  allocateSequentialId(type, prefix) {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    const base = this.buildSequenceMapFromRecords(businessId);
    const inflightKey = this._inflightKey(businessId, type);
    const inflight = this._inflightAllocations.get(inflightKey) || 0;
    const currentYear = new Date().getFullYear();
    const currentSequence = base.get(type) || 0;
    const nextSequence = currentSequence + inflight + 1;
    this._inflightAllocations.set(inflightKey, inflight + 1);
    const paddedSequence = nextSequence.toString().padStart(6, '0');
    return `${prefix}-${currentYear}-${paddedSequence}`;
  }

  /**
   * Hauptmethode für sequenzielle ID-Generierung (Peek — kein vorzeitiges Persist).
   */
  generateSequentialId(type, prefix) {
    return this.peekNextSequentialId(type, prefix);
  }

  /** Nach erfolgreichem Speichern: Zähler aus echten Datensätzen nachziehen. */
  syncSequencesAfterPersist(businessId) {
    this.syncSequencesFromBusinessPayload(
      businessId || getCurrentBusinessId({ allowFallback: false })
    );
  }

  /** Nach ID-Generierung: Sequenz in KV/IndexedDB flushen. */
  async flushSequences() {
    await flushKvBackend();
  }

  /**
   * Generiert Datum-basierte ID nach ISO 8601
   * Format: YYYYMMDD-NNNNNN
   */
  generateDateBasedId(type, prefix) {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const dateString = `${year}${month}${day}`;
    
    const currentSequence = this.sequences.get(`${type}_${dateString}`) || 0;
    const nextSequence = currentSequence + 1;
    
    // Sequenz aktualisieren
    this.sequences.set(`${type}_${dateString}`, nextSequence);
    this.saveSequences();
    
    const paddedSequence = nextSequence.toString().padStart(6, '0');
    const id = `${prefix}-${dateString}-${paddedSequence}`;
    
    console.log(`ISO ID Generator: Generated date-based ${type} ID:`, id);
    return id;
  }

  /**
   * Generiert Business Unit basierte ID
   * Format: BU-YYYY-NNNNNN
   */
  generateBusinessUnitId(type, businessUnit = 'BU01') {
    const currentYear = new Date().getFullYear();
    const key = `${businessUnit}_${type}`;
    const currentSequence = this.sequences.get(key) || 0;
    const nextSequence = currentSequence + 1;
    
    this.sequences.set(key, nextSequence);
    this.saveSequences();
    
    const paddedSequence = nextSequence.toString().padStart(6, '0');
    const id = `${businessUnit}-${type}-${currentYear}-${paddedSequence}`;
    
    console.log(`ISO ID Generator: Generated business unit ${type} ID:`, id);
    return id;
  }

  /**
   * Speichert Sequenzen in KV
   */
  saveSequences() {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    if (!businessId) return;
    const sequenceKey = businessId;

    try {
      const sequencesObject = Object.fromEntries(this.sequences);
      kvSet(`sequences_${sequenceKey}`, sequencesObject);
    } catch (error) {
      console.error('Error saving sequences:', error);
    }
  }

  /**
   * Setzt Sequenz für einen bestimmten Typ zurück
   */
  resetSequence(type) {
    this.sequences.set(type, 0);
    this.saveSequences();
    console.log(`ISO ID Generator: Reset sequence for ${type}`);
  }

  /**
   * Facture-Zähler auf 0 — nächste Nummer = YYYY.001 / CON YYYY.001
   */
  resetInvoiceSequencesToZero(year = new Date().getFullYear()) {
    this.reloadSequences();
    const y = Number(year) || new Date().getFullYear();
    this.sequences.set(normalInvoiceSequenceKey(y), 0);
    this.sequences.set(consolidatedInvoiceSequenceKey(y), 0);
    this.sequences.set('INVOICE', 0);
    this.saveSequences();
    return {
      year: y,
      nextNormal: formatNormalInvoiceNumber(y, 1),
      nextConsolidated: formatConsolidatedInvoiceNumber(y, 1)
    };
  }

  /**
   * Setzt alle Sequenzen zurück
   */
  resetAllSequences() {
    this.sequences.clear();
    this.initializeDefaultSequences();
    console.log('ISO ID Generator: Reset all sequences');
  }

  /**
   * Gibt aktuelle Sequenz-Statistiken zurück
   */
  getSequenceStats() {
    const stats = {};
    for (const [type, sequence] of this.sequences.entries()) {
      stats[type] = {
        currentSequence: sequence,
        nextId: sequence + 1
      };
    }
    return stats;
  }

  /**
   * Validiert ISO-konforme ID
   */
  validateISOId(id, expectedPrefix) {
    const pattern = new RegExp(`^${expectedPrefix}-\\d{4}-\\d{6}$`);
    return pattern.test(id);
  }

  /**
   * Validiert alle französischen ID-Formate
   */
  validateFrenchISOId(id) {
    const patterns = {
      'VENTE': /^VENTE-\d{4}-\d{6}$/,
      'ACHAT': /^ACHAT-\d{4}-\d{6}$/,
      'CLIENT': /^CLIENT-\d{4}-\d{6}$/,
      'FOUR': /^FOUR-\d{4}-\d{6}$/,
      'CMD': /^CMD-\d{4}-\d{6}$/,
      'TRAV': /^TRAV-\d{4}-\d{6}$/,
      'FACT': /^FACT-\d{4}-\d{6}$/,
      'QUITT': /^QUITT-\d{4}-\d{6}$/,
      'DEP': /^DEP-\d{4}-\d{6}$/
    };

    for (const [prefix, pattern] of Object.entries(patterns)) {
      if (pattern.test(id)) {
        return { valid: true, type: prefix };
      }
    }
    
    return { valid: false, type: null };
  }

  /**
   * Migriert von zufälligen zu sequenziellen IDs
   */
  migrateToSequentialIds() {
    console.log('ISO ID Generator: Starting migration to sequential IDs');
    
    // Hier würde die Migration von bestehenden zufälligen IDs
    // zu sequenziellen IDs implementiert werden
    
    return {
      success: true,
      message: 'Migration to sequential IDs completed',
      stats: this.getSequenceStats()
    };
  }
}

// Singleton-Instanz erstellen
const isoStandardIdGenerator = new ISOStandardIdGenerator();

export default isoStandardIdGenerator;
